import type { HttpError } from '@packages/domain/constants/http';
import {
  ACCEPT_JSON_HEADER,
  GET_METHOD,
  HTTPStatuses,
  REQUEST_ID_HEADER,
  REQUESTER_HEADER,
} from '@packages/domain/constants/http';
import {
  fromUnix,
  future,
  isBefore,
  MINUTES,
} from '@packages/domain/utils/date/date';
import {
  isHttpErrorWithStatus,
  isSessionExpiredError,
} from '@packages/domain/utils/errors/errors';
import { refreshToken } from 'actions/authentication/authentication';
import { cleanUnauthorizedUserData } from 'actions/entities/user/user';
import { camelizeKeys } from 'humps';
import type { RootState } from 'reducers/types';
import {
  getAccessToken,
  getExpiresIn,
  isLoggedIn,
} from 'selectors/entities/authentication/authentication';
import { store } from 'store/configureStore';
import { v4 } from 'uuid';

const SIX_SECONDS = 6 * 1000;

type FetchOptions = {
  Accept?: string;
  attempts?: number;
  body?: BodyInit;
  camelize?: boolean;
  delay?: number;
  headers?: HeadersInit;
  maxRetries?: number;
  method?: string;
  timeout?: number;
};

type FetchResponse = {
  payload: unknown;
};

type WindowWithRequester = typeof window & {
  requester: string;
};

const defaultOptions: FetchOptions = {
  method: 'GET',
  timeout: SIX_SECONDS,
  Accept: ACCEPT_JSON_HEADER,
  maxRetries: 2,
  delay: 500,
  attempts: 0,
};

const fetchWithAuthentication = async <ReturnType = void>(
  url: string,
  options: FetchOptions = {},
) => {
  if (store && store.get()) {
    const state = store.get()?.getState();

    if (isLoggedIn(state)) {
      const authHeaders = await getAuthHeaders(state);
      options.headers = {
        ...(authHeaders.Authorization && authHeaders),
        ...(options.headers && options.headers),
      };
    }
  }

  return fetchJson<ReturnType>(url, options).catch(handleFetchError);
};

/**
 * @deprecated better use fetchWithAuthentication, this one should be for internal usage only
 * @param url the url to fetch
 * @param param1 the fetch options
 * @returns the response
 */
const fetchJson = async <ReturnType = unknown>(
  url: string,
  { camelize = true, ...options }: FetchOptions = {},
): Promise<ReturnType> => {
  const requester = (window as WindowWithRequester).requester;
  const headers = {
    ...(!(options.body instanceof FormData)
      ? {
          'Content-Type': ACCEPT_JSON_HEADER,
          'Access-Control-Allow-Origin': '*',
          mode: 'cors',
        }
      : {}),
    ...options.headers,
    [REQUEST_ID_HEADER]: v4(),
    ...(requester && { [REQUESTER_HEADER]: requester }),
  };
  const fetchOptions = { ...defaultOptions, ...options, headers };
  const { method, maxRetries = 2, attempts = 0 } = fetchOptions;

  try {
    const response = await fetch(url, fetchOptions);

    const json = await checkResponse(response, camelize);

    return camelize ? (camelizeJSON(json) as ReturnType) : json;
  } catch (error) {
    if (isHttpErrorWithStatus(error)) {
      const { status } = error as HttpError;
      if (
        status &&
        status >= 500 &&
        method === GET_METHOD &&
        attempts < maxRetries
      ) {
        return retry(url, fetchOptions);
      }
    }
    throw error;
  }
};

const checkResponse = (response: Response, camelize: boolean) => {
  if (response.status === HTTPStatuses.NO_CONTENT) {
    return Promise.resolve({});
  }

  return response.json().then(
    json => {
      if (response.ok) {
        return Promise.resolve(json);
      } else {
        const error: HttpError = new Error(`${response.status}`);
        error.status = response.status;
        error.parsedBody = camelize ? camelizeJSON(json) : json;

        return Promise.reject(error);
      }
    },
    reason => {
      const error: HttpError = new Error(`${response.status} ${reason}`);
      error.status = response.status;
      error.response = response;
      throw error;
    },
  );
};

const camelizeJSON = (json: Record<string, unknown>[]) =>
  camelizeKeys<Record<string, unknown>[]>(json, (key, convert) => {
    return /^[A-Z0-9_]+$/.test(key) ? key : convert(key);
  });

const retry = async <ReturnType = unknown>(
  url: string,
  fetchOptions: FetchOptions,
): Promise<ReturnType> => {
  const { attempts = 0, delay = 100 } = fetchOptions;
  const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
  const incrementedAttempts = attempts + 1;

  await wait(incrementedAttempts * delay);
  return fetchJson(url, {
    ...fetchOptions,
    attempts: incrementedAttempts,
  });
};

const handleFetchError = (
  error: { status?: number; parsedBody?: { errors?: { type: string }[] } } = {},
) => {
  const { status } = error;

  if (store && store.get()) {
    const dispatch = store.get()?.dispatch;

    if (status === HTTPStatuses.UNAUTHORIZED && isSessionExpiredError(error)) {
      // @ts-expect-error actions/store are not properly typed
      dispatch?.(cleanUnauthorizedUserData());
    }
  }

  throw error;
};

const getAuthHeaders = async (state: RootState) => {
  try {
    const accessToken = isTokenExpired(state)
      ? await getNewToken()
      : getAccessToken(state);

    return { Authorization: `Bearer ${accessToken}` };
  } catch (_e) {
    const dispatch = store.get()?.dispatch;
    // @ts-expect-error actions/store are not properly typed
    dispatch?.(cleanUnauthorizedUserData());

    return {};
  }
};

const isTokenExpired = (state: RootState) => {
  const expiresIn = getExpiresIn(state);

  if (!expiresIn) {
    return false;
  }

  const oneMinuteFromNow = future(1, MINUTES);
  const expirationDate = fromUnix(expiresIn);

  return isBefore(expirationDate, oneMinuteFromNow);
};

const getNewToken = async () => {
  // @ts-expect-error actions/store are not properly typed
  const { payload } = await store.get()?.dispatch?.(refreshToken());
  const { accessToken } = payload;

  return accessToken;
};

export {
  type FetchOptions,
  type FetchResponse,
  fetchJson,
  fetchWithAuthentication,
  type WindowWithRequester,
};
